/**
 * EmailHeaderFooterAdmin.tsx — Admin > Forms/Templates > Header & Footer.
 *
 * Legacy's E-mail Header & Footer Templates
 * (admin/admin_email_header_footer_template_list.php): the HTML wrapped above
 * and below an email's body, which tasks, auto responders and the compose
 * window pick from. In the Lead Sources layout (see components/admin/AdminList.tsx):
 *   - Title (its description under it), A–Z until the column is clicked
 *     (no drag to reorder since 2026-10-07: pickers list them
 *     alphabetically); search by title or description; Show deleted;
 *   - "Add header & footer" top right; a click on a live row opens it; Edit /
 *     Delete on a live row, Restore on a deleted one;
 *   - legacy's form: Title *, Description, Header and Footer (the email editor,
 *     with shortcodes and images), and Preview (the header, a sample body, the
 *     footer).
 *
 * Locked (plan 2026-10-10-001 U7: R8, R18): no Add (AdminListPage), row icons
 * or Actions column, and the empty list does not invite adding; search and
 * Show deleted still work, and a click opens a template read-only
 * (AdminFormModal's viewer) where Preview still works.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { Textarea } from '@/components/ui/Textarea';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { DialogField, fieldClasses } from '@/components/admin/DialogChrome';
import { RichTextEditor } from '@/components/admin/RichTextEditor';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort } from '@/components/admin/useListSort';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { getErrorMessage } from '@/services/apiClient';
import {
  HEADER_FOOTER_DESCRIPTION_MAX,
  HEADER_FOOTER_NAME_MAX,
  createHeaderFooter,
  deleteHeaderFooter,
  headerFooterPreviewHtml,
  listHeaderFooters,
  restoreHeaderFooter,
  updateHeaderFooter,
  type AdminHeaderFooter,
} from '@/services/emailHeaderFootersAdmin.service';
import { toast } from '@/store/toastStore';

const COLUMNS: AdminListColumn[] = [{ key: 'name', header: 'Title', sortable: true }, ACTIONS_COLUMN];
/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

function templateName(t: AdminHeaderFooter): string {
  return t.name.trim() || `Header & footer #${t.id}`;
}

export function EmailHeaderFooterAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [templates, setTemplates] = useState<AdminHeaderFooter[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<AdminHeaderFooter | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AdminHeaderFooter | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setTemplates(await listHeaderFooters());
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load headers & footers'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const query = search.trim().toLowerCase();
  const rows = useMemo(
    () =>
      templates
        .filter(
          (t) =>
            (showDeleted || t.active) &&
            (query === '' || t.name.toLowerCase().includes(query) || t.description.toLowerCase().includes(query)),
        )
        .sort((a, b) => compareText(templateName(a), templateName(b)) || a.id - b.id),
    [templates, showDeleted, query],
  );
  const sorting = useListSort(rows, { initialKey: 'name', compare: { name: (a, b) => compareText(templateName(a), templateName(b)) } });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showDeleted] });

  async function handleDelete() {
    if (!deleting) return;
    const { id } = deleting;
    try {
      await deleteHeaderFooter(id);
      setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, active: false } : t)));
      toast.success('Header & footer deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The header & footer could not be deleted.'));
    }
  }

  async function handleRestore(t: AdminHeaderFooter) {
    try {
      await restoreHeaderFooter(t.id);
      toast.success('Header & footer restored');
      void load(true);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The header & footer could not be restored.'));
    }
  }

  return (
    <AdminListPage
      title="Email Header & Footer"
      subtitle="The HTML wrapped above and below an email. Tasks, auto responders and new emails can use one."
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          Add header &amp; footer
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search headers & footers..."
          searchLabel="Search headers and footers"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="header-footer-show-deleted"
        />
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading headers & footers…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={query ? 'No headers & footers found' : 'No headers & footers yet'}
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add a header & footer to get started.'}
        />
      ) : (
        <>
          <AdminListTable
            columns={locked ? LOCKED_COLUMNS : COLUMNS}
            aria-label="Email headers and footers"
            sort={sorting.sort}
            onSortChange={sorting.onSortChange}
          >
            {pagination.pageItems.map((t) => {
              const deleted = !t.active;
              const name = templateName(t);
              return (
                <tr
                  key={t.id}
                  data-row-id={t.id}
                  className={adminRowClass({ deleted, clickable: !deleted })}
                  onClick={deleted ? undefined : () => setEditing(t)}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowName deleted={deleted} nameTestId="header-footer-name">
                      {name}
                    </AdminRowName>
                    {t.description && <div className="mt-0.5 max-w-xl truncate text-xs text-slate-400">{t.description}</div>}
                  </td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestore(t)} />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setEditing(t)} />
                            <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeleting(t)} />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['header & footer', 'headers & footers']} />
        </>
      )}

      {editing && (
        <HeaderFooterFormModal
          key={editing === 'new' ? 'new' : editing.id}
          template={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(true);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete "${templateName(deleting)}"?` : ''}
        message="Tasks and auto responders can no longer pick it. You can restore it from Show deleted."
        confirmLabel="Delete header & footer"
        variant="danger"
      />
    </AdminListPage>
  );
}

function HeaderFooterFormModal({
  template,
  onClose,
  onSaved,
}: {
  template: AdminHeaderFooter | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = template === null;
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm(
    () => ({
      name: template?.name ?? '',
      description: template?.description ?? '',
      header: template?.header ?? '',
      footer: template?.footer ?? '',
    }),
    { tracking: !isNew },
  );
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const missing = t.form.name.trim() === '';

  async function save() {
    if (missing || locked) return;
    setSaving(true);
    try {
      if (template) await updateHeaderFooter(template.id, t.form);
      else await createHeaderFooter(t.form);
      toast.success(isNew ? 'Header & footer created' : 'Header & footer updated');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The header & footer could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Header & Footer' : 'Edit Header & Footer'}
      isNew={isNew}
      dirty={isNew ? Object.values(t.form).some((v) => v.trim() !== '') : t.dirty}
      saving={saving}
      errors={missing ? ['Title is required'] : []}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="xl"
      footerExtra={
        <Button variant="secondary" leftIcon={<Eye className="h-4 w-4" />} onClick={() => setPreviewing(true)}>
          Preview
        </Button>
      }
    >
      <div className="grid gap-4">
        <DialogField
          label="Title"
          htmlFor="header-footer-name"
          required
          state={missing ? 'error' : 'none'}
          labelHint={missing ? '— required' : undefined}
          badge={t.badge('name', 'Title')}
        >
          <Input
            id="header-footer-name"
            value={t.form.name}
            placeholder="e.g. Practice letterhead"
            maxLength={HEADER_FOOTER_NAME_MAX}
            onChange={(e) => t.patch({ name: e.target.value })}
            className={fieldClasses.input(missing ? 'error' : 'none')}
          />
        </DialogField>
        <DialogField label="Description" htmlFor="header-footer-description" badge={t.badge('description', 'Description')}>
          <Textarea autoResize
            id="header-footer-description"
            rows={2}
            value={t.form.description}
            placeholder="Where this one is used"
            maxLength={HEADER_FOOTER_DESCRIPTION_MAX}
            onChange={(e) => t.patch({ description: e.target.value })}
          />
        </DialogField>
        <DialogField label="Header" badge={t.badge('header', 'Header')} description="Shown above the email's body.">
          <RichTextEditor id="header-footer-header" value={t.form.header} onChange={(header) => t.patch({ header })} shortcodes />
        </DialogField>
        <DialogField label="Footer" badge={t.badge('footer', 'Footer')} description="Shown below the email's body.">
          <RichTextEditor id="header-footer-footer" value={t.form.footer} onChange={(footer) => t.patch({ footer })} shortcodes />
        </DialogField>
      </div>

      <Modal isOpen={previewing} onClose={() => setPreviewing(false)} title="Template Preview" size="xl">
        <iframe
          title="Header and footer preview"
          sandbox=""
          srcDoc={headerFooterPreviewHtml(t.form.header, t.form.footer)}
          className="h-[65vh] w-full rounded border border-slate-200 bg-white"
        />
      </Modal>
    </AdminFormModal>
  );
}
